/**
 * BLU-1519: the Product Sync job page's "some variants have no stock yet"
 * notice. Coverage now links every good variant and keeps only these
 * pending, so the page lists each one with its reason in plain words, the
 * fix, and a link to the variant in Shopify admin. Admins get Check again,
 * which runs the coverage pass now instead of at its next retry.
 *
 * Presentational: the page owns the mutation and passes `onCheckAgain`.
 * Amber is the attention colour (DESIGN.md §1); every row pairs it with text.
 */
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { InventoryCoverageStats } from '../../lib/apply-types';
import { stuckReasonKeys, stuckVariantAdminUrl } from '../../lib/coverage-stuck';

export interface CoverageStuckVariantsProps {
  coverage: InventoryCoverageStats;
  /** The org's `*.myshopify.com` domain; no domain means no admin links. */
  shopifyDomain: string | null | undefined;
  /** Only admins may run Product Sync, so only admins see Check again. */
  admin: boolean;
  checking: boolean;
  onCheckAgain: () => void;
}

export function CoverageStuckVariants({ coverage, shopifyDomain, admin, checking, onCheckAgain }: CoverageStuckVariantsProps) {
  const { t } = useTranslation('productSync');
  const rows = coverage.stuckVariants ?? [];
  const waiting = coverage.stuckVariantCount ?? rows.length;
  const total = coverage.approvedVariantCount;
  const linked = total !== undefined ? Math.max(0, total - waiting) : undefined;
  // Only a new Product sync clears what is left, so PrintRun stopped checking.
  const stopped = coverage.autoRetry === false;

  const countLine = total === undefined
    ? t('job.inventoryCoverage.stuckWaitingNoTotal', { count: waiting })
    : t('job.inventoryCoverage.stuckCountLine', {
      waiting: t('job.inventoryCoverage.stuckWaiting', { count: waiting, total }),
      linked: linked === 0
        ? t('job.inventoryCoverage.stuckNoneLinked')
        : t('job.inventoryCoverage.stuckLinked', { count: linked }),
    });

  return (
    <div className="space-y-3">
      <div
        className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50/70 px-4 py-3 text-amber-900"
        role="status"
        aria-live="polite"
      >
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
            <p className="text-sm font-semibold">{t('job.inventoryCoverage.stuckTitle')}</p>
            {admin && !stopped && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 bg-background px-3 text-xs"
                onClick={onCheckAgain}
                disabled={checking}
              >
                {checking
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                  : <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />}
                {t('job.inventoryCoverage.checkAgain')}
              </Button>
            )}
          </div>
          <p className="mt-0.5 text-xs text-amber-800">{countLine}</p>
          <p className="mt-0.5 text-xs text-amber-800">
            {t(stopped ? 'job.inventoryCoverage.stuckNeedsSyncNote' : 'job.inventoryCoverage.stuckRetryNote')}
          </p>
        </div>
      </div>

      {rows.length > 0 && (
        <ul
          className="divide-y rounded-lg border bg-background"
          aria-label={t('job.inventoryCoverage.stuckListLabel')}
        >
          {rows.map((row) => {
            const keys = stuckReasonKeys(row);
            const url = stuckVariantAdminUrl(shopifyDomain, row);
            const variantName = row.variantTitle
              ?? t('job.inventoryCoverage.variantFallback', { id: row.variantId.split('/').pop() ?? row.variantId });
            return (
              <li key={row.variantId} className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <p className="break-words text-sm font-medium">
                    {row.productTitle ?? t('job.inventoryCoverage.unknownProduct')}
                  </p>
                  <p className="mt-0.5 break-words text-xs text-muted-foreground">
                    {variantName}
                    {' · '}
                    {row.sku
                      ? <span className="font-mono">{row.sku}</span>
                      : t('job.inventoryCoverage.noSku')}
                  </p>
                  <div className="mt-1.5 flex items-start gap-1.5 text-xs">
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-amber-600" aria-hidden="true" />
                    <p className="min-w-0">
                      <span className="font-medium">
                        {t(keys.reason, { detail: row.detail ?? '' })}
                      </span>{' '}
                      <span className="text-muted-foreground">{t(keys.action)}</span>
                    </p>
                  </div>
                </div>
                {url && (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex shrink-0 items-center gap-1 rounded-md text-xs font-medium text-primary-strong hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    {t('job.inventoryCoverage.openInShopify')}
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {rows.length > 0 && waiting > rows.length && (
        <p className="text-xs text-muted-foreground">
          {t('job.inventoryCoverage.stuckListTruncated', { shown: rows.length, total: waiting })}
        </p>
      )}
    </div>
  );
}
